import React, { FC } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';
import Modal from '@material-ui/core/Modal';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { OutageLookupResponse } from '../../../../__generated__/pge-types';
import OutageLocationLookup from '../outage-report-popup/OutageLocationLookup';
import {
  NotificationRequestAccountParams,
  OutageFindAccountData,
} from '../../OutageCenterPage';
import { useIsMobile } from '../../../../util/style-utils';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../bottom-drawer-layout/BottomDrawerLayout';
import { Divider } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      display: 'flex',
      width: '922px',
      maxHeight: '77.21vh',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: theme.spacing(3.5, 3, 6.25, 3),
      backgroundColor: colors.modalBackgroundColor,
      overflow: 'auto',
      borderRadius: '5px',
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '90%',
        padding: theme.spacing(3),
      },
    },
    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
    title: {
      lineHeight: '2.25rem',
    },
    modalBody: {},
    kickerText: {
      paddingBottom: theme.spacing(1.875),
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    accountText: {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1),
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    lowerCase: {
      textTransform: 'lowercase',
    },
    bottomSheet: {
      padding: '20px 20px 0',
      paddingTop: 0,
      '& $title': {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: '1.625rem',
        color: colors.noirBlurLight,
      },
    },
    divider: {
      marginTop: '1rem',
      marginBottom: '1rem',
    },
  }),
);

type Props = {
  data: OutageFindAccountData;
  onClose: () => void;
  outageLookUpResult: (data: OutageLookupResponse) => void;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
  isOtherLocation: boolean;
  reportOutageForLoggedIn: () => void;
};

const OutageFindAccountPopup: FC<Props> = ({
  data,
  onClose,
  outageLookUpResult,
  setLoggedInUserNotificationRequestAccountParams,
  isOtherLocation,
  reportOutageForLoggedIn,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();

  const renderAccountPopup = () => {
    return (
      <div className={isMobile ? classes.bottomSheet : classes.modalWrapper}>
        {!isMobile && (
          <div className={classes.closeButton}>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={onClose}
              data-testid="close-icon"
            >
              <Close />
            </IconButton>
          </div>
        )}
        <Grid className={classes.modalBody}>
          <Typography
            className={classes.title}
            variant={isMobile ? 'h2' : 'h1'}
          >
            {t('OUTAGE_FIND_ACCOUNT')}
          </Typography>
          {isMobile && <Divider className={classes.divider} />}
          <Typography variant={'body1'} className={classes.accountText}>
            {`${t(
              'COULD_NOT_FIND_ACCOUNT',
            )} ${data?.lookUpType.toLowerCase()} ${data?.lookUpValue}`}
          </Typography>
          <Typography variant={'body1'} className={classes.kickerText}>
            {`${t('ALTERNATE_FIND_ACCOUNT_TEXT')} `}
            {isMobile ? (
              <a
                href={'tel:' + t('CALL_FOR_ASSISTANCE_NUMBER1')}
                style={{
                  textDecoration: 'underline',
                  color: colors.brightBlue,
                }}
              >
                {t('CALL_FOR_ASSISTANCE_NUMBER1')}
              </a>
            ) : (
              t('CALL_FOR_ASSISTANCE_NUMBER1')
            )}
            .
          </Typography>
          <OutageLocationLookup
            isFindAccount={true}
            onClose={onClose}
            outageLookUpResult={outageLookUpResult}
            setLoggedInUserNotificationRequestAccountParams={
              setLoggedInUserNotificationRequestAccountParams
            }
            accontNumber={''}
            name={''}
            primaryContactNumber={''}
            isOtherLocation={isOtherLocation}
            reportOutageForLoggedIn={reportOutageForLoggedIn}
          />
        </Grid>
      </div>
    );
  };

  return isMobile ? (
    <BottomDrawerLayout
      onClose={onClose}
      data-testid={'find-account-outage-modal'}
      refId={9}
      defaultSnap={DrawerSnapType.default}
    >
      {renderAccountPopup()}
    </BottomDrawerLayout>
  ) : (
    <Modal
      aria-labelledby="find-account-outage-modal"
      aria-describedby="find-account-outage-modal"
      open={data?.isOpen || false}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      {renderAccountPopup()}
    </Modal>
  );
};

export default OutageFindAccountPopup;
